import React, { useState } from 'react';
import { Typography, CircularProgress, Alert } from '@mui/material';
import { useQuery, useMutation, gql } from '@apollo/client';
import { SaveIndicator } from '@personal-dashboard/ui';
import Layout from '../../components/Layout';
import { RecipeToolbar } from './components/RecipeToolbar';
import { RecipeList } from './components/RecipeList';
import RecipeFormDialog, { RecipeFormValues } from './components/RecipeFormDialog';
import { DeleteRecipeDialog } from './components/DeleteRecipeDialog';

export const GET_RECIPES = gql`
  query GetRecipes {
    getRecipes {
      id
      title
      instructions
      ingredients
    }
  }
`;

export const CREATE_RECIPE = gql`
  mutation CreateRecipe($input: RecipeInput!) {
    createRecipe(input: $input) {
      id
      title
      instructions
      ingredients
    }
  }
`;

export const UPDATE_RECIPE = gql`
  mutation UpdateRecipe($id: ID!, $input: RecipeInput!) {
    updateRecipe(id: $id, input: $input) {
      id
      title
      instructions
      ingredients
    }
  }
`;

export const DELETE_RECIPE = gql`
  mutation DeleteRecipe($id: ID!) {
    deleteRecipe(id: $id)
  }
`;

interface Recipe {
  id: string;
  title: string;
  instructions?: string;
  ingredients: string[];
}

const Recipes: React.FC = () => {
  const { data, loading, error } = useQuery(GET_RECIPES);

  const [createRecipe] = useMutation(CREATE_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES }],
  });

  const [updateRecipe] = useMutation(UPDATE_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES }],
  });

  const [deleteRecipe] = useMutation(DELETE_RECIPE, {
    refetchQueries: [{ query: GET_RECIPES }],
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<Recipe | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const handleOpenAdd = () => {
    setEditingRecipe(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRecipe(null);
  };

  const onSubmit = (values: RecipeFormValues) => {
    setSaveStatus('saving');
    handleCloseDialog();

    const mutation = editingRecipe
      ? updateRecipe({
          variables: {
            id: editingRecipe.id,
            input: {
              title: values.title,
              instructions: values.instructions,
              ingredients: values.ingredients,
            },
          },
        })
      : createRecipe({
          variables: {
            input: {
              title: values.title,
              instructions: values.instructions,
              ingredients: values.ingredients,
            },
          },
        });

    mutation
      .then(() => {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      })
      .catch(() => {
        setSaveStatus('error');
      });
  };

  const handleOpenDelete = (recipe: Recipe) => {
    setRecipeToDelete(recipe);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDelete = () => {
    setRecipeToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (recipeToDelete) {
      try {
        await deleteRecipe({
          variables: { id: recipeToDelete.id },
        });
        handleCloseDelete();
      } catch (err) {
        console.error('Error deleting recipe:', err);
      }
    }
  };

  const recipes: Recipe[] = data?.getRecipes || [];
  const filteredRecipes = recipes.filter(r =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.instructions && r.instructions.toLowerCase().includes(searchQuery.toLowerCase())) ||
    r.ingredients.some(ing => ing.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading recipes: {error.message}</Alert>;

  return (
    <Layout>
      <RecipeToolbar onAdd={handleOpenAdd} onSearchChange={setSearchQuery} />

      <SaveIndicator status={saveStatus} />

      <RecipeList
        recipes={filteredRecipes}
        searchQuery={searchQuery}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
      />

      <RecipeFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
        recipe={editingRecipe}
      />

      <DeleteRecipeDialog
        open={deleteConfirmOpen}
        recipeTitle={recipeToDelete?.title}
        onConfirm={handleConfirmDelete}
        onCancel={handleCloseDelete}
      />
    </Layout>
  );
};

export default Recipes;
